
import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Import default styles
import { Calendar as CalendarIcon } from 'lucide-react'; // Import the calendar icon from lucide-react
import styled from 'styled-components';

// Define the types for the props
interface GenericDatePickerProps {
  label: string; // Label text for the date picker
  value: Date | null; // Selected date value
  onChange: (date: Date | null) => void; // Change handler
}

// Styled component for the wrapper
const DatePickerWrapper = styled.div`
  width: 100%; // Full width container
  min-width: 200px; // Minimum width for larger screens
  margin-bottom: 1rem; // Space between elements
  position: relative;
  z-index: 1;

  &:focus-within {
    z-index: 4500;
  }

  .react-datepicker-popper {
    z-index: 4600 !important;
  }

  .react-datepicker {
    border: 1px solid #d6dee7;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.16);
  }

  @media (max-width: 576px) {
    min-width: 100%; // Full width on mobile
  }
`;

// Styled component for the date picker label
const DatePickerLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #000; // Black color for label text
  margin-bottom: 0.5rem;
  display: block; // Ensures the label is above the date picker
`;

// Styled component for the date picker input field (custom style)
const DatePickerInputWrapper = styled.div`
  position: relative; // To position the icon inside the input
  width: 100%; // Full width container for the input

  @media (max-width: 576px) {
    width: 100%; // Full width on mobile
  }
`;

const DatePickerInput = styled.input`
  width: 100%; // Full width input field
  padding: 10px 12px; // Padding for better spacing
  background-color: #f8f9fa; // Light background
  border: 1px solid #ccc; // Subtle border
  border-radius: 8px; // Rounded corners
  color: #000; // Black text color
  font-size: 1rem;
  box-shadow: none; // Remove default box shadow

  &:focus {
    border-color: #aaa; // Slightly darker border on focus
    box-shadow: none; // Remove default focus shadow
  }

  &:hover {
    background-color: #e9ecef; // Change background on hover
  }

  padding-left: 40px; // Leave space for the calendar icon
`;

const StyledCalendarIcon = styled(CalendarIcon)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
`;

// Main DatePicker component
const GenericDatePicker: React.FC<GenericDatePickerProps> = ({ label, value, onChange }) => {
  return (
    <DatePickerWrapper>
      {/* Label for the Date Picker */}
      <DatePickerLabel>{label}</DatePickerLabel>

      {/* Custom styled Date Picker Input */}
      <DatePickerInputWrapper>
        <DatePicker
          selected={value}
          onChange={(date: Date | null) => onChange(date)}
          customInput={<DatePickerInput />} // Use custom input field with styled-components
          dateFormat="yyyy-MM-dd" // Specify the date format
        />
        {/* Add the calendar icon inside the input */}
        <StyledCalendarIcon />
      </DatePickerInputWrapper>
    </DatePickerWrapper>
  );
};

export default GenericDatePicker;
